"use client"

import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { API_BASE_URL } from "@/lib/api"

interface UserInfo {
  id: string
  userId: string
  name: string
  referralCode: string
}

interface Transaction {
  id: string
  userId: string
  amount: number
  type: string
  agent: UserInfo | null
  distributor: UserInfo | null
  createdAt: string
  user: {
    userId: string
    name: string
  }
}

export function TransactionHistory() {
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(false)
  const [typeFilter, setTypeFilter] = useState("all")

  const fetchTransactions = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('auth_token')
      const url = `${API_BASE_URL}/transactions${typeFilter !== "all" ? `?type=${typeFilter}` : ''}`
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setTransactions(data.transactions || [])
      }
    } catch (error) {
      console.error('Error fetching transactions:', error)
    } finally {
      setLoading(false)
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'DEPOSIT': return 'bg-green-500'
      case 'COMMISSION': return 'bg-blue-500'
      case 'WITHDRAW': return 'bg-red-500'
      default: return 'bg-gray-500'
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'DEPOSIT': return 'Deposit'
      case 'COMMISSION': return 'Commission'
      case 'WITHDRAW': return 'Withdraw'
      default: return type
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTransactionDescription = (transaction: Transaction) => {
    switch (transaction.type) {
      case 'DEPOSIT':
        return `${transaction.user.name} deposited ₹${transaction.amount}`
      case 'COMMISSION':
        if (transaction.agent && transaction.distributor) {
          return `Commission from ${transaction.user.name}'s deposit - Agent: ${transaction.agent.name}, Distributor: ${transaction.distributor.name}`
        } else if (transaction.agent) {
          return `Commission from ${transaction.user.name}'s deposit - Agent: ${transaction.agent.name}`
        }
        return `Commission from ${transaction.user.name}'s deposit`
      default:
        return `${transaction.type} transaction`
    }
  }

  useEffect(() => {
    fetchTransactions()
  }, [typeFilter])

  // Calculate totals
  const totalDeposits = transactions
    .filter(t => t.type === 'DEPOSIT')
    .reduce((sum, t) => sum + t.amount, 0)
  
  const totalCommissions = transactions
    .filter(t => t.type === 'COMMISSION')
    .reduce((sum, t) => sum + t.amount, 0)

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Transaction History</CardTitle>
            <CardDescription>
              Complete overview of all transactions, commissions, and financial activities
            </CardDescription>
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Transactions</SelectItem>
              <SelectItem value="DEPOSIT">Deposits</SelectItem>
              <SelectItem value="COMMISSION">Commissions</SelectItem>
              <SelectItem value="WITHDRAW">Withdrawals</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-600">₹{totalDeposits}</div>
              <div className="text-sm text-muted-foreground">Total Deposits</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-600">₹{totalCommissions}</div>
              <div className="text-sm text-muted-foreground">Total Commissions</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{transactions.length}</div>
              <div className="text-sm text-muted-foreground">Total Transactions</div>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading transactions...</div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No transactions found
          </div>
        ) : (
          <div className="space-y-4">
            {transactions.map((transaction) => (
              <Card key={transaction.id} className="p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  {/* Left Section - Basic Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge className={getTypeColor(transaction.type)}>
                        {getTypeLabel(transaction.type)}
                      </Badge>
                      <div className={`text-lg font-semibold ${
                        transaction.type === 'DEPOSIT' ? 'text-green-600' : 
                        transaction.type === 'COMMISSION' ? 'text-blue-600' : 'text-gray-600'
                      }`}>
                        {transaction.type === 'DEPOSIT' ? '+' : ''}₹{transaction.amount}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {getTransactionDescription(transaction)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDate(transaction.createdAt)}
                    </p>
                  </div>

                  {/* Right Section - Detailed Info */}
                  <div className="flex flex-col gap-2 text-sm">
                    {/* User Info */}
                    <div className="flex items-center gap-2">
                      <span className="font-medium">User:</span>
                      <span>{transaction.user.name}</span>
                      <Badge variant="outline" className="text-xs">
                        {transaction.user.userId}
                      </Badge>
                    </div>

                    {/* Commission Details */}
                    {transaction.type === 'COMMISSION' && (
                      <>
                        {transaction.agent && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Agent:</span>
                            <span>{transaction.agent.name}</span>
                            <Badge variant="outline" className="text-xs bg-blue-50">
                              {transaction.agent.userId}
                            </Badge>
                          </div>
                        )}
                        {transaction.distributor && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Distributor:</span>
                            <span>{transaction.distributor.name}</span>
                            <Badge variant="outline" className="text-xs bg-green-50">
                              {transaction.distributor.userId}
                            </Badge>
                          </div>
                        )}
                      </>
                    )}

                    {/* Transaction ID */}
                    <div className="text-xs text-muted-foreground font-mono">
                      ID: {transaction.id.slice(-8)}
                    </div>
                  </div>
                </div>

                {/* Referral Codes for Commission Transactions */}
                {transaction.type === 'COMMISSION' && (transaction.agent || transaction.distributor) && (
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex flex-wrap gap-4 text-xs">
                      {transaction.agent && (
                        <div>
                          <span className="font-medium">Agent Referral:</span>{' '}
                          <code className="bg-gray-100 px-1 rounded">{transaction.agent.referralCode}</code>
                        </div>
                      )}
                      {transaction.distributor && (
                        <div>
                          <span className="font-medium">Distributor Referral:</span>{' '}
                          <code className="bg-gray-100 px-1 rounded">{transaction.distributor.referralCode}</code>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}